/**
 * Conversión entre hexadecimal y HSV para el selector de color.
 *
 * El selector se maneja en HSV porque es como se dibuja: el cuadro es
 * saturación (eje x) por brillo (eje y) y la barra es el tono. Lo que se guarda
 * sigue siendo el hexadecimal; HSV solo vive mientras se arrastra.
 */

/** Tono 0–360, saturación y brillo 0–1. */
export interface Hsv {
  h: number;
  s: number;
  v: number;
}

/** `#rrggbb` en minúsculas. */
export function hsvToHex({ h, s, v }: Hsv): string {
  const channel = (n: number) => {
    const k = (n + h / 60) % 6;
    const value = v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
    return Math.round(value * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(5)}${channel(3)}${channel(1)}`;
}

/** Lee un `#rrggbb`; quien llama ya lo ha validado. */
export function hexToHsv(hex: string): Hsv {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  let h = 0;
  if (delta) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max ? delta / max : 0, v: max };
}
